import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Form, Button, FormControl, InputGroup, Modal, Table } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import { getAllUsers } from '../../services/Users/userService';
import PaginationComponent from '../Pagination/PaginationComponent';

const UserInput = ({ value, onChange, label, isInvalid }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(value || { id: null, username: "" });
  const [searchText, setSearchText] = useState(value?.username || "");
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const previousFilterParams = useRef({});
  const [filterParams, setFilterParams] = useState({});
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    setSelectedUser(value || { id: null, username: "" });
    setSearchText(value?.username || "");
    setIsReadOnly(!!value?.id);
  }, [value]);

  const fetchUsers = useCallback(async () => {
    if (!shouldFetch) return;

    try {
      const data = await getAllUsers(page - 1, pageSize);
      setUsers(data.content || []);
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
    }
  }, [shouldFetch, page, pageSize]);

  useEffect(() => {
    if (shouldFetch) {
      fetchUsers();
    }
  }, [page, pageSize, fetchUsers, shouldFetch]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const filter = { username: searchText };
      handleFilter(filter);
      setShowModal(true);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchText(user.username);
    setIsReadOnly(true);
    onChange({ id: user.id, username: user.username, name: user.username });
    setShowModal(false);
    setShouldFetch(false);
  };

  const handleClear = () => {
    setSelectedUser({ username: "", id: null });
    setSearchText("");
    setIsReadOnly(false);
    onChange(null);
  };

  const handleFilter = (filters) => {
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);
  
    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      previousFilterParams.current = filters;
      setShouldFetch(true);
    }
  };  

  const handleCloseModal = () => {
    if (!selectedUser.id) {
      setSearchText("");
    }
    setShowModal(false);
    setShouldFetch(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setShouldFetch(true);
  };

  const filteredUsers = users.filter(user => 
    !searchText || 
    user.username?.toLowerCase().includes(searchText.toLowerCase()) ||
    user.fullname?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <>
      <div className="mb-3">
        <Form.Label className="mb-1">{label}</Form.Label>
        <InputGroup className={isInvalid ? "is-invalid" : ""}>
          <FormControl
            placeholder="Selecione um usuário"
            value={searchText}
            isInvalid={isInvalid}
            readOnly={isReadOnly}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete='off'
          />
          <Button variant="outline-secondary" onClick={handleClear}>
            <FaTimes />
          </Button>
        </InputGroup>
        {isInvalid && <div className="invalid-feedback">{label} é obrigatório.</div>}
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Selecionar Usuário</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Nome</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center">Nenhum usuário encontrado</td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSelectUser(user)}
                  >
                    <td>{user.username}</td>
                    <td>{user.fullname}</td>
                    <td>{user.role}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <PaginationComponent
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UserInput;

